import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AtsIndicator, createInferenceKey, DataContext, Inference, Side } from "./dataProvider";
import { PriceType } from '@/types/types';
import { isEqual } from 'lodash';
import { ApiRequest, BaseInferenceRequest, InferenceRequestItem } from './api';
import { getInferenceKey } from './data.utils';
import { useLatest } from 'react-use';



const inferencesEqual = (a: Inference | null, b: Inference | null) => {
  return isEqual(a, b);
};

function unsubscribe(requests: BaseInferenceRequest[], send: ((request: ApiRequest, required?: boolean) => void) | null) {
  if (!send) {
    return;
  }

  const unsubscribeRequests = requests.map(r => {
    return { figi: r.figi, ats_indicator: r.ats_indicator, quantity: r.quantity, rfq_label: r.rfq_label, side: r.side, unsubscribe: true }
  });
  send({ inference: unsubscribeRequests }, false);
}

const useInferences = (inferenceRequests: BaseInferenceRequest[], previousTimestamp?: number) => {
  const { getPrevEoD, inferences: allInferences, send } = useContext(DataContext);
  

  // refs
  const prevInferenceRequestCache = useRef<{ [key: string]: boolean }>({}); // keep state of historical requests so they are not requested multiple times
  const prevInferenceCache = useRef<{ [key: string]: Inference }>({});
  const activeSubscriptionsCache = useRef<{ [key: string]: BaseInferenceRequest }>({});
  const sendRef = useLatest(send);


  const prevEod = getPrevEoD ? getPrevEoD(Date.now()) : null;
  const _previousTimestamp = previousTimestamp ? previousTimestamp : prevEod;

  // clear the prevInferenceCache any time _previousTimestamp changes so it doesn't grow too big
  useEffect(() => {
    prevInferenceCache.current = {};
    prevInferenceRequestCache.current = {};
  }, [_previousTimestamp]);

  const requests = inferenceRequests;

  useEffect(() => {
    if (requests && requests.length && send) {
      const filteredRequests: BaseInferenceRequest[] = [];

      // filter requests so we do not subscribe to the same inference multiple times
      requests.forEach(r => {
        const key = getInferenceKey(r);
        if (!activeSubscriptionsCache.current[key]) {
          filteredRequests.push(r);
          activeSubscriptionsCache.current[key] = r;
        }
      });


      // subscribe to new requests
      send({ inference: filteredRequests.map(r => ({ figi: r.figi, ats_indicator: r.ats_indicator, quantity: r.quantity, side: r.side, rfq_label: r.rfq_label, subscribe: true })) });

      // unsubscribe from old requests that are no longer in the requests array
      const requestsMap = new Map(requests.map(r => [getInferenceKey(r), r]));
      const unsubscribeRequestsKeys = Object.keys(activeSubscriptionsCache.current)
        .filter(key => !requestsMap.has(key));

      const unsubscribeRequests = unsubscribeRequestsKeys.reduce((acc, key) => {
        acc.push(activeSubscriptionsCache.current[key]);
        delete activeSubscriptionsCache.current[key]; // delete from active subscriptions cache so it's requested again next time when needed
        return acc;
      }, [] as BaseInferenceRequest[]);


      unsubscribe(unsubscribeRequests, send);
    }
  }, [requests, send]);

  useEffect(() => {
    return () => {
      // unsubscribe from all active subscriptions on unmount
      unsubscribe(Object.values(activeSubscriptionsCache.current), sendRef.current);
    }
  }, [])

  const [inferences, setInferences] = useState<{ [key: string]: Inference }>({});
  useEffect(() => {
    const newInferences = {} as { [key: string]: Inference };
    requests.forEach(r => {
      const key = getInferenceKey(r);

      const requestInferences = allInferences[key] || [];
      const newestInference = requestInferences.reduce((a: Inference | null, c) => !a || a.date < c.date ? c : a, null);
      if (newestInference) {
        newInferences[`${key}_C`] = newestInference
      }
      const previousInference = _previousTimestamp ? requestInferences.findLast(i => i.date.getTime() === _previousTimestamp) || null : null;
      if (previousInference) {
        newInferences[`${key}_P`] = previousInference
      }
    });
    setInferences(prev => {
      const prevKeys = Object.getOwnPropertyNames(prev);
      const newKeySet = new Set(Object.getOwnPropertyNames(newInferences));

      return prevKeys.length === newKeySet.size &&
        prevKeys.every(k => newKeySet.has(k)) &&
        prevKeys.every(k => inferencesEqual(prev[k], newInferences[k]))
        ? prev : newInferences
    });
  }, [allInferences, _previousTimestamp, requests]);

  useEffect(() => {
    if (_previousTimestamp && send) {
      const previousRequests = requests.reduce((a, c) => {
        const key = getInferenceKey(c);
        if (!inferences[`${key}_P`] && !prevInferenceRequestCache.current[key]) {
          a.push({ figi: c.figi, ats_indicator: c.ats_indicator, quantity: c.quantity, side: c.side, rfq_label: c.rfq_label, timestamp: [(new Date(_previousTimestamp)).toISOString()] });
          prevInferenceRequestCache.current[key] = true;
        }
        return a;
      }, [] as InferenceRequestItem[]);
      if (previousRequests.length) {
        send({ inference: previousRequests });
      }
    }
  }, [inferences, _previousTimestamp, requests, send]);

  const getInference: GetInference = useCallback((figi, atsIndicator, quantity, side, rfq_label, previous = false) => {
    const key = createInferenceKey(figi, atsIndicator, quantity, side, rfq_label);
    if (previous) {
      // if it's a previous inference then check/update prevInferenceCache to prevent a gap
      // when the previous inferences drop out of the global inferences cache and
      // get re-requested
      const inferencesKey = `${key}_P`;
      if (!prevInferenceCache.current[inferencesKey] && inferences[inferencesKey]) {
        prevInferenceCache.current[inferencesKey] = inferences[inferencesKey];
      }
      return prevInferenceCache.current[inferencesKey] as Inference | undefined;
    } else {
      return inferences[`${key}_C`] as Inference | undefined;
    }
  }, [inferences]);

  return [getInference, inferences, prevInferenceCache.current] as const;
}

export type GetInference = (figi: string, atsIndicator: AtsIndicator, quantity: number, side: Side, rfq_label: PriceType, previous?: boolean) => Inference | undefined;

export default useInferences;
